/**
 * Pure viewport math for the process diagram's "focus the active step" behavior.
 *
 * Kept out of the component so the framing logic is unit-testable and the layout
 * effect that drives React Flow stays pure orchestration. See ProcessDiagram for
 * why the viewport is computed by hand instead of using React Flow's fitView.
 */

// Zoom bounds for the canvas. minZoom is lowered from React Flow's 0.5 default
// so the fit can zoom out far enough to show the whole diagram beside the chat.
export const MIN_ZOOM = 0.2;
export const MAX_ZOOM = 2;

// Breathing room left around the framed content, as a fraction of the available
// region per side. The step gets a generous margin so it stays the large visual
// anchor; the whole-diagram fallback uses a tighter one to fit more in.
const STEP_MARGIN = 0.18;
const ALL_MARGIN = 0.08;

interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface FocusViewportInput {
  /** Canvas pane width in px (React Flow store width). */
  paneWidth: number;
  /** Canvas pane height in px (React Flow store height). */
  paneHeight: number;
  /** Px at the left covered by an overlay; the focus is centered right of it. */
  leftInset: number;
  /** The active step's rect, in flow coordinates. */
  step: Rect;
  /** Bounds of the whole diagram, in flow coordinates. */
  bounds: Rect;
}

/** Viewport center point + zoom to pass to React Flow's `setCenter`. */
export interface FocusViewport {
  x: number;
  y: number;
  zoom: number;
}

/**
 * Frame the active step within the region right of a left overlay.
 *
 * Preferred framing keeps the step large and the visual anchor. When the chat
 * leaves too little room to also fit the rest of the diagram at that zoom, we
 * zoom out to the whole-diagram framing instead so nothing hides behind the
 * panel. Whichever framing zooms out more wins.
 */
export function computeFocusViewport({
  paneWidth,
  paneHeight,
  leftInset,
  step,
  bounds
}: FocusViewportInput): FocusViewport {
  // The viewport must fit inside the canvas minus the overlay, so opening or
  // widening the chat shrinks the usable area and zooms the view out.
  const availWidth = paneWidth - leftInset;

  const stepZoom = Math.min(
    (availWidth * (1 - 2 * STEP_MARGIN)) / step.width,
    (paneHeight * (1 - 2 * STEP_MARGIN)) / step.height
  );
  const allZoom = Math.min(
    (availWidth * (1 - 2 * ALL_MARGIN)) / bounds.width,
    (paneHeight * (1 - 2 * ALL_MARGIN)) / bounds.height
  );

  const fitAll = allZoom < stepZoom;
  const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, fitAll ? allZoom : stepZoom));
  const focusX = fitAll ? bounds.x + bounds.width / 2 : step.x + step.width / 2;
  const focusY = fitAll ? bounds.y + bounds.height / 2 : step.y + step.height / 2;

  // setCenter places the focus point at the pane center; nudging it left by half
  // the inset lands it centered in the region right of the overlay.
  return { x: focusX - leftInset / (2 * zoom), y: focusY, zoom };
}
